/** Who receives what → the grouped list of scheduled texts/emails and push notifications for one person, with Text / Email / Push switches. */
import React from 'react';
import { Bell, Mail, Phone } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

function Col({ icon: Icon, label }) {
  return <div className="w-16 text-center text-[10px] font-bold uppercase tracking-wider text-gray-500 inline-flex flex-col items-center gap-0.5"><Icon className="w-3.5 h-3.5" />{label}</div>;
}

function ChannelSwitch({ state, onChange, testId, label }) {
  if (!state?.available) return <div className="w-16 text-center text-gray-300 text-xs">—</div>;
  const blocked = state.needs_mobile;
  return (
    <div className="w-16 flex flex-col items-center gap-0.5" title={blocked ? 'No mobile saved — add one on their profile' : state.auto ? 'Included automatically (profit access)' : ''}>
      <Switch checked={!!state.on} disabled={blocked || !onChange} onCheckedChange={(v) => onChange(v)} aria-label={label} data-testid={testId} />
      {blocked && <span className="text-[9px] text-amber-700 font-semibold">no mobile</span>}
      {!blocked && state.auto && <span className="text-[9px] text-violet-700 font-semibold">auto</span>}
    </div>
  );
}

export function ScheduledGroups({ groups, onToggle, canEdit }) {
  return (
    <div className="space-y-4" data-testid="person-scheduled">
      {groups.map((g) => (
        <section key={g.group} className="bg-white rounded-xl border overflow-hidden" data-testid={`person-group-${g.group.replace(/[^a-z]/gi, '').toLowerCase()}`}>
          <header className="flex items-center px-4 py-2.5 bg-gray-50 border-b">
            <h3 className="flex-1 text-sm font-bold text-gray-900">{g.group}</h3>
            <Col icon={Phone} label="Text" /><Col icon={Mail} label="Email" />
          </header>
          <ul className="divide-y">
            {g.jobs.map((j) => (
              <li key={j.key} className={`flex items-center px-4 py-2.5 gap-2 ${j.enabled ? '' : 'opacity-60'}`} data-testid={`person-job-${j.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900">{j.label}{!j.enabled && <span className="ml-2 text-[10px] font-bold uppercase text-gray-400">switched off</span>}</div>
                  <div className="text-[11px] text-gray-500">{j.note || j.what}</div>
                </div>
                <ChannelSwitch state={j.text} onChange={canEdit ? (v) => onToggle(j.key, 'text', v) : null} testId={`person-text-${j.key}`} label={`${j.label} text`} />
                <ChannelSwitch state={j.email} onChange={canEdit ? (v) => onToggle(j.key, 'email', v) : null} testId={`person-email-${j.key}`} label={`${j.label} email`} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function PushGroups({ groups, onToggle, canEdit }) {
  return (
    <div className="space-y-3" data-testid="person-push">
      {groups.map((g) => (
        <section key={g.group} className="bg-white rounded-xl border overflow-hidden">
          <header className="flex items-center px-4 py-2 bg-gray-50 border-b">
            <h3 className="flex-1 text-sm font-bold text-gray-900">{g.group}</h3>
            <Col icon={Bell} label="Push" />
          </header>
          <ul className="divide-y">
            {g.events.map((e) => (
              <li key={e.key} className="flex items-center px-4 py-2 gap-2" data-testid={`person-push-${e.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900">{e.label}</div>
                  <div className="text-[11px] text-gray-500">{e.desc}</div>
                </div>
                <div className="w-16 flex justify-center"><Switch checked={!!e.on} disabled={!canEdit} onCheckedChange={(v) => onToggle(e.key, 'push', v)} aria-label={`${e.label} push`} data-testid={`person-push-switch-${e.key}`} /></div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
